import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { CustomDashAssistantEntityType } from './global-ai-assistant-api';
import type {
  DashboardPageStreamEvent,
  GlobalAssistantConversationPayload,
} from './global-ai-run-page-stream';
import { runDashboardPageStreamCreate } from './global-ai-run-page-stream';

export type HeadlessGeneratedPage = {
  content: LLMTabContentForDispatch;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
};

type PageStreamCreateParams = Parameters<
  typeof runDashboardPageStreamCreate
>[0];

/**
 * What the build resolves its filters under. The on-page build reads these off
 * the page's live filter map; a headless build has no page yet, so each one
 * left out resolves under the server default instead — geography against the
 * METRO tree, role/skill/activity ids under the default taxonomies, and no
 * activity extraction at all without `activitiesEnabled`. The taxonomies are
 * required keys so a caller states `undefined` (server default) on purpose.
 */
export type HeadlessBuildTaxonomy = {
  [K in
    | 'rolesTax'
    | 'skillsTax'
    | 'activitiesTax'
    | 'geoTax']: PageStreamCreateParams[K];
} & Pick<PageStreamCreateParams, 'activitiesEnabled'>;

/**
 * Run the create-mode page-gen stream HEADLESSLY (no mounted builder) and
 * collect the FINAL layout + resolved filters (resolve-first Phase C).
 * The dispatch is a no-op and the stream's own filter apply is captured
 * instead of executed — application happens later, on the seeded landing.
 *
 * Returns null on failure OR when the stream asked clarification questions
 * (callers fall back to their legacy flow — never a dead end). Progress
 * events are forwarded so each call site can render them its own way (the
 * turn runner routes them into the activity lane; the click-time path shows
 * a generation status).
 *
 * An ABORT is neither of those: it throws (an `AbortError` DOMException).
 * Collapsing it into the `null` failure shape made an aborted build read as
 * "generation failed → fall back to the legacy resume flow", resurrecting a
 * build the user just cancelled. Two abort paths need covering: the fetch
 * layer REJECTS when the signal fires between requests, but the SSE read loop
 * exits GRACEFULLY on a mid-stream abort (`while (!signal?.aborted)` in
 * global-ai-run-page-stream.ts) — so the post-await aborted check below is
 * load-bearing, not defensive.
 */
export async function generateDashboardHeadless(params: {
  prompt: string;
  entityType: CustomDashAssistantEntityType;
  conversation?: GlobalAssistantConversationPayload;
  /**
   * Required, never defaulted: an administrative ask ("Texas", "Kent") built
   * under the metro tree, or an activity ask built without activity
   * extraction, silently lands the page on its DEFAULT entities.
   */
  taxonomy: HeadlessBuildTaxonomy;
  onProgress?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<HeadlessGeneratedPage | null> {
  try {
    let lastConfig: LLMTabContentForDispatch | undefined;
    let lastFilters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
    let sawQuestions = false;
    await runDashboardPageStreamCreate({
      prompt: params.prompt,
      entityType: params.entityType,
      ...(params.conversation ? { conversation: params.conversation } : {}),
      ...params.taxonomy,
      // Committed build: the user chose to build, so the generation must
      // best-guess rather than bail to a clarifying question mid-build (the
      // `sawQuestions` fallback below then stays a pure safety net).
      allowClarification: false,
      dispatch: () => undefined,
      applyAiSearchFilters: () => Promise.resolve(),
      onEvent: (event) => {
        if (event.type === 'config') {
          lastConfig = event.content;
        } else if (event.type === 'filters') {
          lastFilters = event.content;
        } else if (event.type === 'questions') {
          sawQuestions = true;
        }
        params.onProgress?.(event);
      },
      ...(params.signal ? { signal: params.signal } : {}),
    });
    // Mid-stream abort: the SSE read loop exits without throwing, so surface
    // the abort explicitly — a partially-streamed layout must not be treated
    // as either a finished page or a generation failure.
    if (params.signal?.aborted) {
      throw new DOMException('The headless page-gen was aborted', 'AbortError');
    }
    if (sawQuestions || lastConfig === undefined) {
      return null;
    }
    return {
      content: lastConfig,
      ...(lastFilters ? { filters: lastFilters } : {}),
    };
  } catch (err) {
    // Abort ≠ failure: rethrow so callers abandon quietly instead of falling
    // back to the legacy resume flow. `signal.aborted` is authoritative (any
    // throw after the abort is cancellation fallout); the name check catches a
    // fetch-layer AbortError racing ahead of the flag.
    if (
      params.signal?.aborted ||
      (err instanceof DOMException && err.name === 'AbortError')
    ) {
      throw err;
    }
    return null;
  }
}
